import classNames from "classnames";
import { inject } from "mobx-react";
import qs from "query-string";
import { type FunctionComponent, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { Outlet, Route } from "react-router-dom";
import { withRouter, type WithRouterProps } from "nvent-web/utils/withRouter";
import { compose } from "nvent-web/utils/compose";

import PageTitle from "nvent-web/App/components/PageTitle";
import { type KnownLocale, knownLocales } from "nvent-web/i18n";
import { UserStore } from "nvent-web/stores/User";

import style from "./IndependentSelectionGuide.module.scss";
import { InsulationStep } from "./InsulationStep";
import { RecommendationsStep } from "./RecommendationsStep";
import { RoomDetailsStep } from "./RoomDetailsStep";

interface IndependentSelectionGuideProps {
  fullWidth?: boolean;
  createProjectDisabled?: boolean;
}

interface IndependentSelectionGuideInnerProps
  extends WithRouterProps, IndependentSelectionGuideProps {
  user: UserStore;
}

const LayoutComponent: FunctionComponent<
  IndependentSelectionGuideInnerProps
> = ({ user, fullWidth }) => {
  useEffect(() => {
    const { lang } = qs.parse(window.location.search);

    if (!lang || Array.isArray(lang) || lang === user.locale) {
      return;
    }

    const newLocale = lang.toLowerCase() as KnownLocale;

    if (knownLocales.includes(newLocale)) {
      user.setLocale(newLocale);
    }
  }, [user]);

  return (
    <div
      className={classNames(style.container, {
        [style.fullWidthContainer]: fullWidth,
      })}
    >
      <PageTitle>
        <FormattedMessage id="selectionGuide.title" />
      </PageTitle>
      <div className={style.content}>
        <Outlet />
      </div>
    </div>
  );
};

LayoutComponent.displayName = "LayoutComponent";

const Layout = compose<IndependentSelectionGuideProps>(
  inject("user"),
  withRouter
)(LayoutComponent);

export const independentSelectionGuideRoutes = ({
  fullWidth,
  createProjectDisabled,
}: IndependentSelectionGuideProps) => (
  <Route path="selection-guide" element={<Layout fullWidth={fullWidth} />}>
    <Route index element={<InsulationStep fullWidth={fullWidth} />} />
    <Route
      path="insulation"
      element={<InsulationStep fullWidth={fullWidth} />}
    />
    <Route path="room" Component={RoomDetailsStep} />
    <Route
      path="recommendations"
      element={
        <RecommendationsStep createProjectDisabled={createProjectDisabled} />
      }
    />
  </Route>
);
